import {
  ArrowHeadRightIcon,
  Button,
  Card,
  ControlIcon,
  DeleteIcon,
  Dropdown,
  DropdownMenu,
  EditIcon,
  ExclamationmarkIcon,
  HandIcon,
  LockIcon,
  PinIcon,
  QuestionmarkIcon,
  RoomIcon,
  Tooltip,
  UnlockIcon,
  UnpinIcon,
} from '@bp/ui-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { niceDate } from '../../../utils/dateCalculations';
import { getWarningTypeTranslation } from '../../../utils/getWarningTypeTranslation';
import { getRoomLabelWithBuilding } from '../../../utils/roomLabel';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { formatDeviationDays } from '../cardLengthDeviation';
import { TeachingBlockCard } from '../TeachingBlockCard/TeachingBlockCard';
import styles from './CardInfo.module.scss';

export const CardInfo: FC = observer(() => {
  const { t } = useTranslation();

  const store = useTeachingBlockStore();

  const card = store.selectedCard;
  const warnings = card ? store.getWarningsForCard(card.uuid) : [];
  const children = store.virtualCards.filter((vc) => vc.parentUuid === card?.uuid);
  const lengthDeviationDays = card ? store.getCardLengthDeviationDays(card) : 0;

  const { classesData, groupsData, roomsData } = useLoadBasicData({
    pause: false,
  });

  const cardTitles = card
    ? (card?.subject?.name ?? '-')
    : store.selectedRows.length > 0
      ? store.gridContext === 'subjects'
        ? store.selectedRows.map((value) => value.label).join(', ')
        : '-'
      : '-';

  const cardClasses = card
    ? (card?.classes.map((c) => classesData?.classes.find((aClass) => aClass.uuid === c.uuid)?.name).join(', ') ?? '-')
    : store.selectedRows.length > 0
      ? store.gridContext === 'classes'
        ? store.selectedRows.map((value) => value.label).join(', ')
        : '-'
      : '-';

  const cardTeachers = card
    ? (card?.teachers.map((t) => t.displayNameShort).join(', ') ?? '-')
    : store.selectedRows.length > 0
      ? store.gridContext === 'teachers'
        ? store.selectedRows.map((value) => value.label).join(', ')
        : '-'
      : '-';

  const cardRooms = card
    ? (card?.rooms
        .map((r) => {
          const room = roomsData?.rooms.find((room) => room.uuid === r.uuid);
          return room ? getRoomLabelWithBuilding(room.name, room.building?.name) : undefined;
        })
        .join(', ') ?? '-')
    : store.selectedRows.length > 0
      ? store.gridContext === 'rooms'
        ? store.selectedRows.map((value) => getRoomLabelWithBuilding(value.label, value.subLabel)).join(', ')
        : '-'
      : '-';

  const cardGroups =
    card?.lessonClasses
      .map((lessonClass) => {
        return lessonClass.groups
          .map((group) => groupsData?.groups.find((g) => g.uuid === group.uuid)?.name)
          .join(', ');
      })
      .filter((s) => !!s)
      .join(' / ') ?? '-';

  function onPick(event: MouseEvent<HTMLDivElement>) {
    if (card && !card.locked && card.uuid !== store.pickedCard?.card.uuid) {
      store.pickCard(card, { x: event.clientX, y: event.clientY });
    } else {
      store.pickCard(null);
    }
  }

  function onPin() {
    if (card) {
      store.pinCard(card);
    }
  }

  function onLock() {
    if (card) {
      if (card.locked) {
        store.unlockCard(card);
      } else {
        store.lockCard(card);
      }
    }
  }

  function onDiscard() {
    if (card) store.discardCard(card);
  }

  return (
    <Card
      wrapperClass={classNames(styles['card-info'], {
        // [styles['control-mode']]: pinboardStore.controlMode.isActive,
      })}
      className={styles['content-wrapper']}
      contentPadding='none'
      fitContent
      canScroll={true}
    >
      <div className={styles['actions-wrapper']}>
        <Tooltip content={store.pickedCard?.card === card ? t('pinboard.actions.unpick') : t('pinboard.actions.pick')}>
          <Button
            disabled={store.readonly || !card || card.loading || (card.locked ?? false)}
            icon={<HandIcon />}
            hierarchy='tertiary'
            onClick={(e) => onPick(e as MouseEvent<HTMLDivElement>)}
          />
        </Tooltip>
        <Tooltip content={card?.pinned ? t('pinboard.actions.unpin') : t('pinboard.actions.pin')}>
          <Button
            disabled={store.readonly || !card || card.loading}
            icon={card?.pinned ? <UnpinIcon /> : <PinIcon />}
            hierarchy='tertiary'
            onClick={onPin}
          />
        </Tooltip>
        <Tooltip content={card?.locked ? t('pinboard.actions.unlock') : t('pinboard.actions.lock')}>
          <Button
            icon={card?.locked ? <UnlockIcon /> : <LockIcon />}
            disabled={store.readonly || !card || card.loading || (!!card && !card.startDate)}
            hierarchy='tertiary'
            onClick={onLock}
          />
        </Tooltip>
        <Tooltip content={t('pinboard.actions.showConditions')}>
          <Button
            // disabled={!card || card.loading}
            disabled
            hierarchy='tertiary'
            icon={<ControlIcon />}
            // onClick={() => {
            //   pinboardStore.setConstraintModalOpen(true);
            // }}
          />
        </Tooltip>
        {(() => {
          const editDropdown = (
            <Dropdown
              usePortal={!store.isFullscreen}
              trigger={
                <Button
                  icon={<EditIcon />}
                  disabled={store.readonly || !card || card.locked || !card.startDate}
                  hierarchy='tertiary'
                />
              }
            >
              <DropdownMenu
                usePortal={!store.isFullscreen}
                data={[
                  {
                    label: t('pinboard.actions.editRooms'),
                    onClick: () =>
                      store.setCardRoomsModal({
                        isOpen: true,
                        card: card,
                      }),
                  },
                  {
                    label: t('pinboard.actions.editTime'),
                    onClick: () =>
                      store.setCardDurationModal({
                        isOpen: true,
                        card: card ?? undefined,
                      }),
                  },
                ]}
              />
            </Dropdown>
          );

          // Im Fullscreen klappt der Tooltip nach unten über das Menü (Button hängt oben am
          // Bildschirmrand), daher hier ohne Tooltip rendern.
          return store.isFullscreen ? (
            editDropdown
          ) : (
            <Tooltip content={t('pinboard.actions.edit')}>{editDropdown}</Tooltip>
          );
        })()}
        <Tooltip content={t('common.delete')}>
          <Button
            icon={<DeleteIcon />}
            disabled={store.readonly || !card || card.loading || !card.startDate || card.locked}
            hierarchy='tertiary'
            onClick={onDiscard}
          />
        </Tooltip>
        <div className={styles['info-icons']}>
          {card && card.rooms.length === 0 && (
            <Tooltip triggerClass={styles.info} content={t('pinboard.reason.noRoom')}>
              <RoomIcon />
            </Tooltip>
          )}
          {card &&
            store.highlightMode.includes('warnings') &&
            (warnings.length > 0 || (card.availabilityWarnings && card.availabilityWarnings.length > 0)) && (
              <Tooltip
                triggerClass={styles.warning}
                content={warnings
                  .map((warning) => {
                    return `${getWarningTypeTranslation(warning.type)} (${warning.data?.map((d) => d.name)})`;
                  })
                  .join(', ')}
              >
                <ExclamationmarkIcon />
              </Tooltip>
            )}
          {card && children.length > 0 && (
            <Tooltip
              triggerClass={styles.info}
              content={
                <>
                  <div>{t('teachingBlockVersion.splitCardHint')}</div>
                  <div>{t('teachingBlockVersion.actualDurationHint', { duration: card.duration ?? 1 })}</div>
                </>
              }
            >
              <QuestionmarkIcon />
            </Tooltip>
          )}
        </div>
      </div>

      <div className={styles['preview-wrapper']}>
        {card ? (
          <div className={styles['card-overview']}>
            {!card.startDate ? (
              <div className={styles['split-info']}>
                <TeachingBlockCard card={card} onClick={onPick} passive />
                <div
                  className={styles.dates}
                  style={{
                    maxWidth: `calc(var(--badge-card-width) + var(--badge-card-grow-factor) * ${(card.duration ?? 1) / 7})`,
                  }}
                >
                  <div className={styles.start}>
                    {t('teachingBlockVersion.durationInWeeks', { weeks: card.duration })}
                  </div>
                </div>
              </div>
            ) : children.length > 0 ? (
              children.map((child, i) => {
                return (
                  <div className={styles.split} key={child.uuid}>
                    <div className={styles['split-info']}>
                      <TeachingBlockCard card={card} duration={child.duration} onClick={(e) => onPick(e)} passive />
                      <div
                        className={styles.dates}
                        style={{
                          maxWidth: `calc(var(--badge-card-width) + var(--badge-card-grow-factor) * ${(card.duration ?? 1) / 7})`,
                        }}
                      >
                        <div className={styles.start}>{niceDate(child.startDateString, 'short')}</div>
                        <div className={styles.end}>{niceDate(child.endDateString, 'short')}</div>
                      </div>
                    </div>
                    {i < children.length - 1 && <ArrowHeadRightIcon className='small' />}
                  </div>
                );
              })
            ) : (
              <div className={styles['split-info']}>
                <TeachingBlockCard card={card} onClick={onPick} passive />
                <div
                  className={styles.dates}
                  style={{
                    maxWidth: `calc(var(--badge-card-width) + var(--badge-card-grow-factor) * ${(card.duration ?? 1) / 7})`,
                  }}
                >
                  <div className={styles.start}>{niceDate(card.startDate, 'short')}</div>
                  <div className={styles.end}>{niceDate(card.endDate, 'short')}</div>
                </div>
              </div>
            )}
            {card && lengthDeviationDays !== 0 && (
              <div className={styles.length}>{formatDeviationDays(lengthDeviationDays, t)}</div>
            )}
          </div>
        ) : store.selectedRows.length > 0 ? (
          store.selectedRows.map((row, index) => {
            if (index <= 10) {
              if (index <= 9) {
                return (
                  <div key={index + String(row.value)} className={styles['selected-class']}>
                    {row.label.slice(0, 2)}
                  </div>
                );
              } else {
                return (
                  <div key={index + String(row.value)} className={styles['selected-class']}>
                    +{store.selectedRows.length - index}
                  </div>
                );
              }
            }
            return <></>;
          })
        ) : (
          <></>
        )}
      </div>

      <div className={styles['info-wrapper']}>
        <div className={styles.row}>
          <div className={styles.title}>{t('subject.title', { count: 1 })}</div>
          <div className={styles.content} title={cardTitles}>
            {cardTitles}
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.title}>{t('classes.title', { count: 1 })}</div>
          <div className={styles.content} title={cardClasses}>
            {cardClasses}
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.title}>{t('persons.title', { count: 1 })}</div>
          <div className={styles.content} title={cardTeachers}>
            {cardTeachers}
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.title}>{t('rooms.title', { count: 1 })}</div>
          <div className={styles.content} title={cardRooms}>
            {cardRooms}
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.title}>{t('common.group_other')}</div>
          <div className={styles.content} title={cardGroups}>
            {cardGroups}
          </div>
        </div>
      </div>
    </Card>
  );
});
